import React from 'react';
import moment from 'moment';
import {Column, EditType} from '@src/model/table';
import {DeviceRow} from '@src/dtos/device.dto';
import {formatPhone} from '@src/utils/phone-util';
import {DevicesStatusCell} from './devices-status-cell';
import {DevicesVpnUsageCell} from './devices-vpn-usage-cell';

const PHONE_REGEX = /^\d{10}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
const EMPLOYEE_NAME_REGEX = /^[A-Za-z]+(?: [A-Za-z]+)*$/;
const EMPLOYEE_NAME_MAX = 25;
const EMAIL_MAX = 50;

const validatePhone = (value: string): string =>
    !value || PHONE_REGEX.test(value) ? '' : 'Enter a valid 10 digit number';

const validateEmail = (value: string): string =>
    !value || EMAIL_REGEX.test(value) ? '' : 'Invalid email address';

const validateEmployeeName = (value: string): string =>
    !value || EMPLOYEE_NAME_REGEX.test(value.trim()) ? '' : 'Letters only';

const isExistingDevice = (row: DeviceRow): boolean => !!row.inviteId;

const sortByVpnUsage = (a: DeviceRow, b: DeviceRow, direction: 'asc' | 'desc'): number => {
    const aUsage = a.vpnUsage?.usagePercent ?? undefined;
    const bUsage = b.vpnUsage?.usagePercent ?? undefined;

    if (aUsage === undefined && bUsage === undefined) {
        return 0;
    }
    if (aUsage === undefined) {
        return 1;
    }
    if (bUsage === undefined) {
        return -1;
    }

    return direction === 'asc' ? aUsage - bUsage : bUsage - aUsage;
};

export const formatLastActive = (lastSeenAt?: string | null): string => {
    if (!lastSeenAt) {
        return '-';
    }
    const lastSeenDate = moment(lastSeenAt);
    if (!lastSeenDate.isValid()) {
        return '-';
    }

    return lastSeenDate.isSame(moment(), 'day')
        ? lastSeenDate.format('h:mm a')
        : lastSeenDate.format('MM/DD/YYYY');
};

export const devicesColumns: Column<DeviceRow>[] = [
        {
            header: 'Device number',
            accessor: 'phoneNumber',
            width: 180,
            isSortable: true,
            displayOnEdit: true,
            type: EditType.Text,
            mandatory: true,
            isKeyField: true,
            showDuplicatedError: true,
            getValue: (row) => formatPhone(row.phoneNumber),
            getPlaceholder: () => 'xxx-xxx-xxxx',
            disableEdit: isExistingDevice,
            validationRules: [validatePhone],
            getDisplayElement: (_column, row) => formatPhone(row.phoneNumber),
        },
        {
            header: 'Email',
            accessor: 'userEmail',
            width: 250,
            isSortable: true,
            displayOnEdit: true,
            type: EditType.Text,
            maxLength: EMAIL_MAX,
            mandatory: true,
            getPlaceholder: () => 'name@company.com',
            disableEdit: isExistingDevice,
            validationRules: [validateEmail],
        },
        {
            header: 'Employee name',
            accessor: 'userName',
            width: 208,
            isSortable: true,
            displayOnEdit: true,
            type: EditType.Text,
            maxLength: EMPLOYEE_NAME_MAX,
            getPlaceholder: () => 'Employee name',
            validationRules: [validateEmployeeName],
        },
        {
            header: 'Status',
            accessor: 'displayStatus',
            width: 208,
            isSortable: true,
            getDisplayElement: (_column, row) => <DevicesStatusCell row={row}/>,
        },
        {
            header: 'VPN usage',
            accessor: 'vpnUsage',
            width: 296,
            isSortable: true,
            getDisplayElement: (_column, row) => <DevicesVpnUsageCell vpnUsage={row.vpnUsage}/>,
            sortFn: sortByVpnUsage,
        },
        {
            header: 'App version',
            accessor: 'appVersion',
            width: 150,
            isSortable: true,
            getDisplayElement: (_column, row) => row.appVersion ?? '-',
        },
        {
            header: 'Last active',
            accessor: 'lastSeenAt',
            width: 208,
            isSortable: true,
            getDisplayElement: (_column, row) => <>{formatLastActive(row.lastSeenAt)}</>,
        },
];